import { Box, Container, Heading, Link, Text } from '@joinrepublic/dls-ui-kit';
import { observer } from 'mobx-react-lite';

import { CryptoAssets } from '../crypto-assets/crypto-assets';
import { FundedVentures } from '../funded-ventures/funded-ventures';
import { GrowthStageVentures } from '../growth-stage-ventures/growth-stage-ventures';
import { RealWorldAssets } from '../real-world-assets/real-world-assets';

const DUE_DILIGENCE_URL = '/learn/investors/how-we-select-startups';

/**
 * Caps the intro copy at the design's 562px measure, so it wraps after "All
 * companies" into the two lines the mockup shows rather than running the full
 * container width.
 */
const INTRO_MAX_WIDTH = '562px';

/**
 * Shared by the copy and the link inside it, which carries its own size token
 * and would otherwise keep the desktop measure once the copy shrinks.
 */
const INTRO_SIZE = { base: 'body-sm-md', sm: 'body-lg' } as const;

export const InvestmentOpportunities = observer(
  function InvestmentOpportunities() {
    return (
      <Container
        maxWidth="1460px"
        mt={{ base: '6', md: '12' }}
        mb="20"
        // 24px on mobile rather than the design's 20px, so the copy lines up
        // with the global header, which sits at `px="6"` on every page.
        px={{ base: '6', md: '8' }}
      >
        <Heading
          as="h1"
          size={{ base: 'title-lg', sm: 'title-xl' }}
          // `title-lg` only reaches 48px at `xs` (641px), but the design wants
          // 48px at every mobile width, so it's pinned via `fontSize`/`sx` —
          // `HeadingProps` exposes no other typography prop for `lineHeight`.
          fontSize={{ base: '5xl', sm: '7xl' }}
          sx={{ lineHeight: { base: '3rem', sm: '4.5rem' } }}
          noDefaultSpacing
        >
          Investment opportunities
        </Heading>

        {/* The non-breaking spaces keep the link phrase on one line so the copy
          wraps after "All companies", matching the design's two-line shape. */}
        <Text
          mt={{ base: '4', md: '6' }}
          size={INTRO_SIZE}
          variant="text-secondary"
          maxWidth={INTRO_MAX_WIDTH}
        >
          Browse current investment opportunities on Republic. All companies are{' '}
          <Link href={DUE_DILIGENCE_URL} size={INTRO_SIZE} color="inherit">
            vetted&nbsp;&amp;&nbsp;pass&nbsp;due&nbsp;diligence
          </Link>
          .
        </Text>

        {/* The design opens the first section tighter than it spaces the rest. */}
        <Box mt={{ base: '10', md: '18' }}>
          <RealWorldAssets />
        </Box>

        <Box mt="18">
          <CryptoAssets />
        </Box>

        <Box mt="18">
          <GrowthStageVentures />
        </Box>

        <Box mt="18">
          <FundedVentures />
        </Box>
      </Container>
    );
  }
);
